/**
 * IndexedDB Credit Cleanup Utility
 *
 * This utility removes all credit-related data from IndexedDB.
 * Credits should NEVER be persisted in IndexedDB - they must always come from the backend.
 *
 * This cleanup:
 * 1. Removes credit fields from the "plan" settings object
 * 2. Removes any userData with credit information
 * 3. Logs what was cleaned up
 *
 * Usage:
 *   Import and call cleanupCreditsFromIndexedDB() on app initialization
 */

import { IndexedDBService } from '../services/indexeddb.service';

export interface CleanupResult {
  success: boolean;
  itemsCleaned: string[];
  errors: string[];
}

/**
 * Clean up all credit-related data from IndexedDB
 */
export async function cleanupCreditsFromIndexedDB(
  indexedDB: IndexedDBService
): Promise<CleanupResult> {
  const result: CleanupResult = {
    success: true,
    itemsCleaned: [],
    errors: [],
  };

  console.log('🧹 Starting IndexedDB credit cleanup...');

  try {
    // 1. Clean up "plan" settings - remove credit fields
    try {
      const planSettings = await indexedDB.getItem('settings', 'plan');
      if (planSettings) {
        let modified = false;

        if ('credits' in planSettings) {
          delete planSettings.credits;
          modified = true;
          console.log('  ✅ Removed "credits" from plan settings');
        }

        if ('extraCredits' in planSettings) {
          delete planSettings.extraCredits;
          modified = true;
          console.log('  ✅ Removed "extraCredits" from plan settings');
        }

        if (modified) {
          await indexedDB.setItem('settings', 'plan', planSettings);
          result.itemsCleaned.push('plan.credits');
          result.itemsCleaned.push('plan.extraCredits');
        }
      }
    } catch (error: any) {
      const errorMsg = `Failed to clean plan settings: ${error.message}`;
      console.error(`  ❌ ${errorMsg}`);
      result.errors.push(errorMsg);
      result.success = false;
    }

    // 2. Remove any "userData" from "user" store that might contain credits
    try {
      const userData = await indexedDB.getItem('user', 'userData');
      if (userData) {
        let hasCredits = false;

        if ('subscriptionCredits' in userData) {
          hasCredits = true;
        }
        if ('extraCredits' in userData) {
          hasCredits = true;
        }
        if ('credits' in userData) {
          hasCredits = true;
        }

        if (hasCredits) {
          // Remove the entire userData object as it contains credits
          await indexedDB.removeItem('user', 'userData');
          result.itemsCleaned.push('user.userData');
          console.log('  ✅ Removed userData containing credit information');
        }
      }
    } catch (error: any) {
      // User store might not exist, which is fine
      if (!error.message.includes('not found') && !error.message.includes('does not exist')) {
        const errorMsg = `Failed to clean user data: ${error.message}`;
        console.error(`  ❌ ${errorMsg}`);
        result.errors.push(errorMsg);
        result.success = false;
      }
    }

    // 3. Check for any other settings that might have credit data
    try {
      const allSettings = await indexedDB.getAllItems('settings');
      for (const item of allSettings) {
        if (item.key === 'plan') continue; // Already handled

        let modified = false;
        const value = item.value;

        if (value && typeof value === 'object') {
          if ('credits' in value) {
            delete value.credits;
            modified = true;
          }
          if ('subscriptionCredits' in value) {
            delete value.subscriptionCredits;
            modified = true;
          }
          if ('extraCredits' in value) {
            delete value.extraCredits;
            modified = true;
          }

          if (modified) {
            await indexedDB.setItem('settings', item.key, value);
            result.itemsCleaned.push(`settings.${item.key}`);
            console.log(`  ✅ Removed credit fields from settings.${item.key}`);
          }
        }
      }
    } catch (error: any) {
      const errorMsg = `Failed to check all settings: ${error.message}`;
      console.error(`  ❌ ${errorMsg}`);
      result.errors.push(errorMsg);
      result.success = false;
    }

    // Summary
    if (result.itemsCleaned.length > 0) {
      console.log(`✅ IndexedDB cleanup complete. Cleaned ${result.itemsCleaned.length} items:`);
      result.itemsCleaned.forEach((item) => console.log(`   - ${item}`));
    } else {
      console.log('✅ IndexedDB cleanup complete. No credit data found.');
    }

    if (result.errors.length > 0) {
      console.warn(`⚠️ Cleanup completed with ${result.errors.length} error(s):`);
      result.errors.forEach((error) => console.warn(`   - ${error}`));
    }
  } catch (error: any) {
    result.success = false;
    result.errors.push(`Unexpected error during cleanup: ${error.message}`);
    console.error('❌ IndexedDB cleanup failed:', error);
  }

  return result;
}

/**
 * Check if IndexedDB contains any credit-related data
 * Useful for diagnostics
 */
export async function checkForCreditsInIndexedDB(
  indexedDB: IndexedDBService
): Promise<string[]> {
  const foundItems: string[] = [];

  try {
    // Check plan settings
    const planSettings = await indexedDB.getItem('settings', 'plan');
    if (planSettings) {
      if ('credits' in planSettings) {
        foundItems.push('settings.plan.credits');
      }
      if ('extraCredits' in planSettings) {
        foundItems.push('settings.plan.extraCredits');
      }
    }

    // Check user data
    try {
      const userData = await indexedDB.getItem('user', 'userData');
      if (userData) {
        if ('subscriptionCredits' in userData) {
          foundItems.push('user.userData.subscriptionCredits');
        }
        if ('extraCredits' in userData) {
          foundItems.push('user.userData.extraCredits');
        }
        if ('credits' in userData) {
          foundItems.push('user.userData.credits');
        }
      }
    } catch {
      // User store might not exist
    }

    // Check all settings
    const allSettings = await indexedDB.getAllItems('settings');
    for (const item of allSettings) {
      if (item.key === 'plan') continue; // Already checked

      const value = item.value;
      if (value && typeof value === 'object') {
        if ('credits' in value) {
          foundItems.push(`settings.${item.key}.credits`);
        }
        if ('subscriptionCredits' in value) {
          foundItems.push(`settings.${item.key}.subscriptionCredits`);
        }
        if ('extraCredits' in value) {
          foundItems.push(`settings.${item.key}.extraCredits`);
        }
      }
    }
  } catch (error) {
    console.error('Error checking for credits in IndexedDB:', error);
  }

  return foundItems;
}
